import {
  LegacyPortalReason,
  type LegacyPortalReasonMode,
} from "@/components/legacy-portal-reason/legacy-portal-reason";
import {
  type ShopPortalPatch,
  useUpdateShopPortal,
} from "@/api/super-admin/use-super-admin-shop-portal";
import { Switch } from "@babackapp/ui/components/switch";
import { useState } from "react";

/**
 * Toggles the shop's V3 portal opt-in, then refetches the super admin list.
 * Switching off goes through a dialog asking why; the reason then sits next to
 * the switch as a note, as long as the shop stays on the legacy widget.
 */
export function NewPortalSwitch({
  shopId,
  isEnabled,
  legacyPortalReason,
}: {
  shopId: string;
  isEnabled: boolean;
  legacyPortalReason: string | null;
}) {
  const { mutate, isPending } = useUpdateShopPortal(shopId);
  const [dialogMode, setDialogMode] = useState<LegacyPortalReasonMode | null>(
    null,
  );

  const save = (patch: ShopPortalPatch) =>
    mutate(patch, { onSuccess: () => setDialogMode(null) });

  return (
    <div className="flex items-center gap-2">
      <Switch
        checked={isEnabled}
        disabled={isPending}
        onCheckedChange={(next) =>
          next ? save({ enabled: true }) : setDialogMode("switch-off")
        }
        aria-label="Nouveau portail client"
      />
      <span
        className={[
          "text-[11px] font-semibold uppercase tracking-wide",
          isEnabled ? "text-violet-600" : "text-muted-foreground",
        ].join(" ")}
      >
        {isEnabled ? "Nouveau" : "Ancien"}
      </span>
      <LegacyPortalReason
        isEnabled={isEnabled}
        reason={legacyPortalReason}
        mode={dialogMode}
        onModeChange={setDialogMode}
        isPending={isPending}
        onSubmit={(reason, mode) =>
          save(
            mode === "switch-off"
              ? { enabled: false, legacyPortalReason: reason }
              : { legacyPortalReason: reason },
          )
        }
      />
    </div>
  );
}
